import { createTheme } from "@material-ui/core/styles";
import { checkIsInIframe } from "utils/marketingSiteMessageUtils";

// DEVNOTE: Base theme is used across two skins - this and sohoSkinTheme. Please ensure any changes here are compatible with both skins and if not, add to sohoSkinTheme as needed.

const isInIframe = checkIsInIframe();

export const baseTheme = createTheme({
  palette: {
    type: "light",
    background: {
      default: "#252A2E",
      componentBackground1: "#252A2E",
      componentBackground2: "#363E45",
      componentBackground3: "#3F4850",
      onDark: {
        default: "#2C1D28",
      },
    },
    primary: {
      main: "#FF0029",
      light: "#C70021",
      dark: "#FF4766",
      contrastText: "#FCF5E8",
      hover: "rgba(255, 0, 41, 0.04)",
      selected: "rgba(255, 0, 41, 0.08)",
      active: "rgba(255, 0, 41, 0.24)",
      border: "rgba(255, 0, 41, 0.50)",
      containedActive: "rgba(255, 0, 41, 0.24)",
    },
    secondary: {
      main: "#7AAFA6",
      light: "#5A968C",
      dark: "#A6CAC4",
      contrastText: "#FCF5E8",
      hover: "rgba(85, 141, 132, 0.04)",
      selected: "rgba(85, 141, 132, 0.08)",
      active: "rgba(85, 141, 132, 0.24)",
      border: "rgba(85, 141, 132, 0.5)",
      containedActive: "rgba(85, 141, 132, 0.24)",
    },
    info: {
      main: "#89A8BF",
      dark: "#B7CAD8",
      light: "#658DAB",
      contrastText: "#252A2E",
      darkText: "#EBF0F4",
      lightBg: "#20303C",
    },
    success: {
      main: "#7AAFA6",
      dark: "#A6CAC4",
      light: "#5A968C",
      contrastText: "#252A2E",
      darkText: "#ECF4F2",
      lightBg: "#223936",
    },
    error: {
      main: "#F1694E",
      dark: "#F6A190",
      light: "#ED3D1A",
      contrastText: "#252A2E",
      darkText: "#FDE7E3",
      lightBg: "#551407",
    },
    warning: {
      main: "#EAB260",
      dark: "#F2D19F",
      light: "#E39B2E",
      contrastText: "#252A2E",
      darkText: "#FBF2E4",
      lightBg: "#52300A",
      onDark: {
        main: "#EAB260",
        dark: "#F2D19F",
        light: "#E39B2E",
        contrastText: "#252A2E",
        darkText: "#FBF2E4",
        lightBg: "#52300A",
      },
    },
    text: {
      red: "#FF0029",
      heading: "#FCF5E8",
      primary: "rgba(252, 245, 232, 0.87)",
      secondary: "rgba(252, 245, 232, 0.60)",
      disabled: "rgba(252, 245, 232, 0.38)",
      onDark: {
        primary: "rgba(252, 245, 232, 0.87)",
        secondary: "rgba(252, 245, 232, 0.60)",
        disabled: "rgba(252, 245, 232, 0.38)",
      },
    },
    other: {
      backdrop: "rgba(18, 18, 18, 0.54)",
      disabledBackground: "#606263",
      activeRating: "#EAB260",
      dropShadow: "#252A2E",
      imageOverlay: "#121212",
      lineDetail: "rgba(255, 249, 239, 0.20)",
      onDark: {
        backdrop: "rgba(18, 18, 18, 0.54)",
        disabledBackground: "#606263",
        dropShadow: "#252A2E",
        imageOverlay: "#121212",
      },
    },
    grey: {
      0: "#252A2E",
      50: "#393D40",
      100: "#4D5051",
      200: "#606263",
      300: "#747574",
      400: "#888886",
      500: "#9C9B97",
      600: "#B0AEA9",
      700: "#C4C1BA",
      800: "#D7D3CC",
      900: "#EBE6DD",
      1000: "#FFF9EF",
    },
    divider: "rgba(255, 249, 239, 0.20)",

    // Custom mollies colour palette
    custom: {
      offWhite: "#FAF2E1",
      // lightTeal: "#558d84",
      //  neutralTeal: "#A8C0AB",
      //  brightTeal: "#00807A",
      // mustard: "#e19421",
      // coral: "#e63612",
      molliesRed: "#FF0029",
      //  mainGrey: "#20262C",
      nightBlue: "#FCF5E8",
      plum: "#463943",
      greenCountry: "#4e5f4d",
      systemWhite: "#FFFEF0",
    },
  },
  typography: {
    fontFamily: `"Jost", "Helvetica", "Arial", sans-serif`,
    fontOpticalSizing: "auto",
    h1: {
      fontFamily: `Jost, "Helvetica", "Arial", sans-serif`,
      fontSize: "90px",
      lineHeight: "1.2em",
      letterSpacing: "0.08em",
      fontWeight: 400,
      textTransform: "none",
    },
    h2: {
      fontFamily: `Jost, "Helvetica", "Arial", sans-serif`,
      fontSize: "63px",
      lineHeight: "1.25em",
      letterSpacing: "0.08em",
      fontWeight: 400,
      textTransform: "none",
    },
    h3: {
      fontFamily: `Jost, "Helvetica", "Arial", sans-serif`,
      fontSize: "45px",
      lineHeight: "1.3em",
      letterSpacing: "0.06em",
      fontWeight: 400,
      textTransform: "none",
    },
    h4: {
      fontFamily: `Jost, "Helvetica", "Arial", sans-serif`,
      fontSize: "32px",
      lineHeight: "1.4em",
      letterSpacing: "0.06em",
      fontWeight: 400,
      textTransform: "none",
    },
    h5: {
      fontFamily: `Jost, "Helvetica", "Arial", sans-serif`,
      fontSize: "23px",
      lineHeight: "1.5em",
      letterSpacing: "0.08em",
      fontWeight: 600,
      textTransform: "none",
    },
    h6: {
      fontFamily: `"Jost", "Helvetica", "Arial", sans-serif`,
      fontOpticalSizing: "auto",
      fontSize: "19px",
      lineHeight: "1.5em",
      letterSpacing: "0.08em",
      fontWeight: 600,
      textTransform: "none",
    },
    subtitle1: {
      fontFamily: `"Jost", "Helvetica", "Arial", sans-serif`,
      fontOpticalSizing: "auto",
      fontSize: "16px",
      lineHeight: "1.5em",
      letterSpacing: "0.19px",
      fontWeight: 600,
      textTransform: "none",
    },
    subtitle2: {
      fontFamily: `"Jost", "Helvetica", "Arial", sans-serif`,
      fontOpticalSizing: "auto",
      fontSize: "14px",
      lineHeight: "1.5em",
      letterSpacing: "0.02em",
      fontWeight: 600,
      textTransform: "none",
    },
    body1: {
      fontFamily: `"Jost", "Helvetica", "Arial", sans-serif`,
      fontOpticalSizing: "auto",
      fontSize: "18px",
      lineHeight: "1.5em",
      letterSpacing: "0em",
      fontWeight: 400,
    },
    body2: {
      fontFamily: `"Jost", "Helvetica", "Arial", sans-serif`,
      fontOpticalSizing: "auto",
      fontSize: "16px",
      lineHeight: "1.5em",
      letterSpacing: "0em",
      fontWeight: 400,
    },
    body3: {
      fontFamily: `"Jost", "Helvetica", "Arial", sans-serif`,
      fontOpticalSizing: "auto",
      fontSize: "14px",
      lineHeight: "1.5em",
      letterSpacing: "0em",
      fontWeight: 400,
    },
    button: {
      fontFamily: `"Jost", "Helvetica", "Arial", sans-serif`,
      fontOpticalSizing: "auto",
      fontStyle: "normal",
      fontSize: "18px",
      lineHeight: "1.5em",
      letterSpacing: "0.02em",
      textTransform: "none",
      fontWeight: 500,
    },
    button3: {
      fontFamily: `"Jost", "Helvetica", "Arial", sans-serif`,
      fontOpticalSizing: "auto",
      fontStyle: "normal",
      fontSize: "16px",
      lineHeight: "1.5em",
      letterSpacing: "0.02em",
      textTransform: "none",
      fontWeight: 500,
    },
    caption: {
      fontFamily: `"Jost", "Helvetica", "Arial", sans-serif`,
      fontOpticalSizing: "auto",
      fontSize: "12px",
      lineHeight: "1.2em",
      letterSpacing: "0.06em",
      textTransform: "none",
      fontWeight: 400,
    },
    overline: {
      fontFamily: `"Jost", "Helvetica", "Arial", sans-serif`,
      fontOpticalSizing: "auto",
      fontSize: "12px",
      lineHeight: "2.5em",
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      fontWeight: 500,
    },
  },
  shadows: [
    "none", // elevation0
    "0px 1px 1px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 1px 3px rgba(0, 0, 0, 0.12)", // elevation1
    "0px 1px 1px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 2px 5px rgba(0, 0, 0, 0.12)", //evevation2
    "0px 3px 3px rgba(0, 0, 0, 0.2), 0px 3px 4px rgba(0, 0, 0, 0.14), 0px 1px 8px rgba(0, 0, 0, 0.12)", //evevation3
    "0px 2px 4px rgba(0, 0, 0, 0.2), 0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px rgba(0, 0, 0, 0.12)", //evevation4
    "0px 3px 5px rgba(0, 0, 0, 0.2), 0px 5px 8px rgba(0, 0, 0, 0.14), 0px 1px 14px rgba(0, 0, 0, 0.12);", //evevation5
    "0px 3px 5px rgba(0, 0, 0, 0.2), 0px 6px 10px rgba(0, 0, 0, 0.14), 0px 1px 18px rgba(0, 0, 0, 0.12)", //evevation6
    "0px 4px 5px rgba(0, 0, 0, 0.2), 0px 7px 10px rgba(0, 0, 0, 0.14), 0px 2px 16px rgba(0, 0, 0, 0.12)", //evevation7
    "0px 5px 5px rgba(0, 0, 0, 0.2), 0px 8px 10px rgba(0, 0, 0, 0.14), 0px 3px 14px rgba(0, 0, 0, 0.12)", //evevation8
    "0px 5px 6px rgba(0, 0, 0, 0.2), 0px 9px 12px rgba(0, 0, 0, 0.14), 0px 3px 16px rgba(0, 0, 0, 0.12)", //evevation9
    "0px 6px 6px rgba(0, 0, 0, 0.2), 0px 10px 14px rgba(0, 0, 0, 0.14), 0px 4px 18px rgba(0, 0, 0, 0.12)", //evevation10
    "0px 6px 7px rgba(0, 0, 0, 0.2), 0px 11px 15px rgba(0, 0, 0, 0.14), 0px 4px 20px rgba(0, 0, 0, 0.12)", //evevation11
    "0px 7px 8px rgba(0, 0, 0, 0.2), 0px 12px 17px rgba(0, 0, 0, 0.14), 0px 5px 22px rgba(0, 0, 0, 0.12)", //evevation12
    "0px 7px 8px rgba(0, 0, 0, 0.2), 0px 13px 19px rgba(0, 0, 0, 0.14), 0px 5px 24px rgba(0, 0, 0, 0.12)", //evevation13
    "0px 7px 9px rgba(0, 0, 0, 0.2), 0px 14px 21px rgba(0, 0, 0, 0.14), 0px 5px 26px rgba(0, 0, 0, 0.12)", //evevation14
    "0px 8px 9px rgba(0, 0, 0, 0.2), 0px 15px 22px rgba(0, 0, 0, 0.14), 0px 6px 28px rgba(0, 0, 0, 0.12)", //evevation15
    "0px 8px 10px rgba(0, 0, 0, 0.2), 0px 16px 24px rgba(0, 0, 0, 0.14), 0px 6px 30px rgba(0, 0, 0, 0.12)", //evevation16
    "0px 8px 11px rgba(0, 0, 0, 0.2), 0px 17px 26px rgba(0, 0, 0, 0.14), 0px 6px 32px rgba(0, 0, 0, 0.12)", //evevation17
    "0px 9px 11px rgba(0, 0, 0, 0.2), 0px 18px 28px rgba(0, 0, 0, 0.14), 0px 7px 34px rgba(0, 0, 0, 0.12)", //evevation18
    "0px 9px 12px rgba(0, 0, 0, 0.2), 0px 19px 29px rgba(0, 0, 0, 0.14), 0px 7px 36px rgba(0, 0, 0, 0.12)", //evevation19
    "0px 10px 13px rgba(0, 0, 0, 0.2), 0px 20px 31px rgba(0, 0, 0, 0.14), 0px 8px 38px rgba(0, 0, 0, 0.12)", //evevation20
    "0px 10px 13px rgba(0, 0, 0, 0.2), 0px 21px 33px rgba(0, 0, 0, 0.14), 0px 8px 40px rgba(0, 0, 0, 0.12)", //evevation21
    "0px 10px 14px rgba(0, 0, 0, 0.2), 0px 22px 35px rgba(0, 0, 0, 0.14), 0px 8px 42px rgba(0, 0, 0, 0.12)", //evevation22
    "0px 11px 14px rgba(0, 0, 0, 0.2), 0px 23px 36px rgba(0, 0, 0, 0.14), 0px 9px 44px rgba(0, 0, 0, 0.12)", //evevation23
    "0px 11px 15px rgba(0, 0, 0, 0.2), 0px 24px 38px rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12)", //evevation24
  ],
  /* special shadow to match nav bar in mollies.com site */
  navBarShadow: "1px 1px 4px rgb(0 0 0 / 10%)",
  // Custom sidebar breakpoint for embedded iframe in marketing site
  breakpoints: {
    values: {
      sidebar: isInIframe ? 445 : 520,
      tablet: 770,
      desktop: 1110,
    },
  },
  contentSize: {
    pageWidth: "80%",
    maxPageWidth: "1120px",
    maxContentWidth: "1440px",
    mobilePageWidth: "90%",
  },
  shape: {
    borderRadius: "0px",
    buttonRadius: "4px",
  },
  mixins: {
    toolbar: {
      minHeight: 114,
    },
  },
});

// Mobile typography styles
baseTheme.typography = {
  ...baseTheme.typography,
  h2: {
    ...baseTheme.typography.h2,
    [baseTheme.breakpoints.down(baseTheme.breakpoints.values.tablet)]: {
      fontSize: "30px",
      lineHeight: "38px",
    },
  },
  h3: {
    ...baseTheme.typography.h3,
    [baseTheme.breakpoints.down(baseTheme.breakpoints.values.tablet)]: {
      fontSize: "23px",
      lineHeight: "26px",
      letterSpacing: "1px",
    },
  },
  body1: {
    ...baseTheme.typography.body1,
    [baseTheme.breakpoints.down(baseTheme.breakpoints.values.tablet)]: {
      fontSize: "16px",
    },
  },
  subtitle1: {
    ...baseTheme.typography.subtitle1,
    [baseTheme.breakpoints.down(baseTheme.breakpoints.values.tablet)]: {
      fontSize: "16px",
      lineHeight: "23px",
    },
  },
};

baseTheme.overrides.MuiPaper = {
  root: {
    borderRadius: "0px",
    boxShadow: "none",
    backgroundColor: baseTheme.palette.background.default,
  },
  elevation1: {
    boxShadow: "none",
    border: `1px solid ${baseTheme.palette.other.lineDetail}`,
    backgroundColor: baseTheme.palette.background.default,
  },
  elevation8: {
    backgroundColor: baseTheme.palette.background.default,
  },
};

// Causing width issues with Booking Summary component (keep disabled for now)
// baseTheme.overrides.MuiBox = {
//   root: {
//     backgroundColor: baseTheme.palette.background.default,
//   },
// };

baseTheme.overrides.MuiDialog = {
  paper: {
    backgroundColor: baseTheme.palette.background.default,
  },
};

baseTheme.overrides.MuiButton = {
  root: {
    borderRadius: "4px",
    padding: "8px 16px", // medium size
  },
  contained: {
    boxShadow: "none",
    padding: "8px 16px", // medium size
  },
  outlined: {
    padding: "8px 16px", // medium size
  },
  sizeSmall: {
    ...baseTheme.typography.button2,
    padding: "8px 16px",
  },
  sizeLarge: {
    // Mobile style - large doesnt exist on mobile - use medium
    padding: "8px 16px",

    [baseTheme.breakpoints.up(baseTheme.breakpoints.values.tablet)]: {
      padding: "12px 24px",
      borderRadius: "4px",
    },
  },
  containedPrimary: {
    "&:hover": {
      backgroundColor: baseTheme.palette.grey[200],
      boxShadow: "none",
    },
  },
  containedSecondary: {
    "&:hover": {
      backgroundColor: baseTheme.palette.grey[200],
      boxShadow: "none",
    },
  },
};

baseTheme.overrides.MuiInput = {
  root: {
    ...baseTheme.typography.body2,
    fontSize: "16px", // Needed to prevent zoom on ios safari
  },
  underline: {
    "&:before": {
      borderBottomColor: baseTheme.palette.other.lineDetail,
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottomColor: baseTheme.palette.other.lineDetail,
    },
  },
};

baseTheme.overrides.MuiGrid = {
  root: {
    color: baseTheme.palette.text.primary,
  },
};

baseTheme.overrides.MuiListItem = {
  root: {
    color: baseTheme.palette.text.primary,
  },
};

baseTheme.overrides.MuiSelect = {
  root: {
    color: baseTheme.palette.text.primary,
    "&:before": {
      borderBottomColor: baseTheme.palette.other.lineDetail,
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottomColor: baseTheme.palette.other.lineDetail,
    },
  },
  icon: {
    color: baseTheme.palette.text.primary,
  },
};

baseTheme.overrides.MuiOutlinedInput = {
  root: {
    backgroundColor: "#FFFFFF",
    "& $notchedOutline": {
      borderColor: baseTheme.palette.other.lineDetail,
    },
    "&:hover $notchedOutline": {
      borderColor: baseTheme.palette.other.lineDetail,
    },
  },
};

baseTheme.overrides.MuiPopover = {
  paper: {
    backgroundColor: baseTheme.palette.background.default,
  },
};

baseTheme.overrides.MuiFormControl = {
  root: {
    minWidth: "80px",
  },
};

baseTheme.overrides.MuiStepContent = {
  root: {
    borderColor: baseTheme.palette.other.lineDetail,
  },
};

baseTheme.overrides.MuiStepConnector = {
  line: {
    borderColor: baseTheme.palette.other.lineDetail,
  },
};

baseTheme.overrides.MuiStepIcon = {
  root: {
    "&$active": {
      color: baseTheme.palette.primary.main,
    },
    text: {
      fill: baseTheme.palette.text.onDark.primary,
    },
  },
};

export default baseTheme;
